import { useEffect, useRef, useState } from "react";
import { api, fmtDate, openBlob } from "@/lib/api";
import { PCard } from "@/components/portal/PortalBits";
import { Upload, Loader2, FileCheck2, Eye } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { k: "id_front", label: "Government ID (front)" },
  { k: "id_back", label: "Government ID (back)" },
  { k: "selfie", label: "Selfie holding ID" },
  { k: "proof_of_income", label: "Proof of income" },
];
const REVIEW = { Pending: "text-amber-600", Approved: "text-emerald-600", Rejected: "text-rose-600" };

function UploadTile({ kind, label, docs, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const latest = docs.find((d) => d.kind === kind);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Max file size is 8 MB");
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/portal/documents", fd);
      toast.success(`${label} uploaded`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  return (
    <PCard className="flex items-center justify-between gap-3" data-testid={`doc-tile-${kind}`}>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {latest ? (
          <div className={`text-[11px] font-semibold ${REVIEW[latest.review_status]}`}>
            <FileCheck2 className="inline h-3 w-3 mr-1" />
            {latest.review_status} · {fmtDate(latest.created_at)}
          </div>
        ) : (
          <div className="text-[11px] text-slate-400">Not uploaded</div>
        )}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden
        onChange={(e) => upload(e.target.files[0])} data-testid={`doc-input-${kind}`} />
      <button onClick={() => ref.current.click()} disabled={busy} data-testid={`doc-upload-${kind}`}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-xl bg-blue-600 text-white text-xs font-semibold active:scale-95 transition-transform disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {latest ? "Replace" : "Upload"}
      </button>
    </PCard>
  );
}

export default function PortalDocuments() {
  const [docs, setDocs] = useState([]);
  const load = () => api.get("/portal/documents").then((r) => setDocs(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-3" data-testid="portal-documents-page">
      <h1 className="font-display text-2xl font-bold text-slate-900">Verify your identity</h1>
      <p className="text-sm text-slate-500">Upload clear photos (JPG, PNG, WEBP) or PDF, up to 8 MB each. Files are stored securely.</p>
      {KINDS.map((k) => <UploadTile key={k.k} kind={k.k} label={k.label} docs={docs} onDone={load} />)}
      {docs.length > 0 && (
        <PCard className="p-1 mt-4">
          {docs.map((d) => (
            <button key={d.id} onClick={() => openBlob(`/portal/documents/${d.id}/download`)} data-testid={`doc-view-${d.id}`}
              className="w-full flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl text-left">
              <span className="text-sm text-slate-700 truncate">{d.original_filename}</span>
              <Eye className="h-4 w-4 text-slate-400" />
            </button>
          ))}
        </PCard>
      )}
    </div>
  );
}
